import { useMemo, useState } from 'react';
import './PersonCards.css';

export type PersonCardRecord = Record<string, unknown> & {
  key?: string;
  name?: string;
  role?: string;
  email?: string;
  photo?: string;
};

export type PersonCardsData = {
  type: 'person-cards';
  title?: string;
  people: PersonCardRecord[];
};

type PersonGroup = {
  id: string;
  name: string;
  records: PersonCardRecord[];
};

const ignoredFields = new Set(['key', 'name', 'role', 'photo']);

const fieldLabels: Record<string, string> = {
  department: 'Departamento',
  center: 'Centro',
  location: 'Ubicación',
  building: 'Edificio',
  province: 'Provincia',
  site: 'Sede',
  email: 'Email',
};

const formatFieldLabel = (field: string): string =>
  fieldLabels[field] ||
  field
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const getPersonIdentity = (person: PersonCardRecord, index: number): string => {
  const name = displayValue(person.name).trim().toLowerCase();
  const email = displayValue(person.email).trim().toLowerCase();
  return email || name || person.key || `person-${index}`;
};

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const getPhotoSrc = (photo: unknown): string | null => {
  if (typeof photo !== 'string' || !photo.trim()) {
    return null;
  }

  const normalizedPhoto = photo
    .trim()
    .replace(/^data:image\/[^;]+;base64,/i, '')
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(normalizedPhoto)) {
    return null;
  }

  try {
    const decoded = atob(normalizedPhoto);
    const bytes = Uint8Array.from(decoded.slice(0, 12), (character) => character.charCodeAt(0));
    let mimeType = 'image/jpeg';

    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      mimeType = 'image/png';
    } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      mimeType = 'image/gif';
    } else if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      mimeType = 'image/webp';
    }

    return `data:${mimeType};base64,${normalizedPhoto}`;
  } catch {
    return null;
  }
};

const PersonRecordDetails = ({ record }: { record: PersonCardRecord }) => {
  const fields = Object.entries(record).filter(([field, value]) => {
    return !ignoredFields.has(field) && displayValue(value).trim().length > 0;
  });

  return (
    <div className="person-card__details">
      {fields.map(([field, value]) => {
        const renderedValue = displayValue(value);

        if (field === 'email') {
          return (
            <div className="person-card__field" key={field}>
              <span className="person-card__label">{formatFieldLabel(field)}</span>
              <a className="person-card__value person-card__email" href={`mailto:${renderedValue}`}>
                {renderedValue}
              </a>
            </div>
          );
        }

        return (
          <div className="person-card__field" key={field}>
            <span className="person-card__label">{formatFieldLabel(field)}</span>
            <span className="person-card__value">{renderedValue}</span>
          </div>
        );
      })}
    </div>
  );
};

const PersonCard = ({ person }: { person: PersonGroup }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [primaryRecord, ...otherRecords] = person.records;
  const hasMultipleRoles = person.records.length > 1;

  return (
    <article className={`person-card ${hasMultipleRoles ? 'person-card--multiple' : 'person-card--single'}`}>
      <header className="person-card__header">
        <div className="person-card__avatar">
          {getPhotoSrc(primaryRecord.photo) ? (
            <img
              src={getPhotoSrc(primaryRecord.photo) || undefined}
              alt={`Foto de ${person.name}`}
            />
          ) : (
            <span aria-hidden="true">{getInitials(person.name)}</span>
          )}
        </div>
        <div className="person-card__identity">
          <h3>{person.name}</h3>
          {primaryRecord.role && <p>{displayValue(primaryRecord.role)}</p>}
        </div>
      </header>

      <PersonRecordDetails record={primaryRecord} />

      {hasMultipleRoles && (
        <div className="person-card__roles">
          <button
            type="button"
            className="person-card__roles-toggle"
            aria-expanded={isOpen}
            onClick={() => setIsOpen((open) => !open)}
          >
            <span>
              {isOpen
                ? 'Ocultar cargos'
                : `Ver ${otherRecords.length} ${otherRecords.length === 1 ? 'cargo más' : 'cargos más'}`}
            </span>
            <span className={`person-card__chevron ${isOpen ? 'is-open' : ''}`} aria-hidden="true">
              ⌄
            </span>
          </button>

          {isOpen && (
            <div className="person-card__other-roles">
              {otherRecords.map((record, index) => (
                <section className="person-card__role" key={record.key || `${person.id}-role-${index}`}>
                  {record.role && <h4>{displayValue(record.role)}</h4>}
                  <PersonRecordDetails record={record} />
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const PersonCards = ({ data }: { data: PersonCardsData }) => {
  const people = useMemo<PersonGroup[]>(() => {
    const groups = new Map<string, PersonGroup>();

    data.people.forEach((person, index) => {
      const id = getPersonIdentity(person, index);
      const existing = groups.get(id);

      if (existing) {
        existing.records.push(person);
      } else {
        groups.set(id, {
          id,
          name: displayValue(person.name) || 'Persona sin nombre',
          records: [person],
        });
      }
    });

    return Array.from(groups.values());
  }, [data.people]);

  const hasMultiplePeople = people.length > 1;

  return (
    <section className={`person-cards ${hasMultiplePeople ? 'person-cards--multiple' : ''}`}>
      {data.title && <h2 className="person-cards__title">{data.title}</h2>}
      <div className="person-cards__grid">
        {people.map((person) => (
          <PersonCard key={person.id} person={person} />
        ))}
      </div>
    </section>
  );
};

export default PersonCards;
